// Tidsmaskin: spol tilbake gjennom situasjonsbildet og spill av. Tar opp
// snapshots av sporene mens klienten er LIVE (ring-buffer i minnet, kun det
// denne økten faktisk har sett — ingen falsk historikk). Scrubbing/avspilling
// driver kart-renderingen fra opptaket; «LIVE» går tilbake til sanntid.
//
// Full time-machine på tvers av økter krever tidsserie-lagring (PostGIS/
// Timescale) i hubben — se docs/SKADE_ARKITEKTUR.md §3. Dette er økt-lokalt.
import type { Track } from '@skade/contracts';

export interface Frame {
  ts: number;
  tracks: Track[];
}

const SPEEDS = [1, 4, 10, 30];

export class TimeMachine {
  private frames: Frame[] = [];
  private cap = 900; // ~ siste 900 snapshots (rikelig for en økt)
  private live = true;
  private idx = -1;
  private playing = false;
  private speedIdx = 1; // 4x
  private timer?: ReturnType<typeof setInterval>;
  private open = false;

  private el: HTMLElement;
  private slider!: HTMLInputElement;
  private clockEl!: HTMLElement;
  private playBtn!: HTMLButtonElement;
  private liveBtn!: HTMLButtonElement;
  private speedBtn!: HTMLButtonElement;

  constructor(
    container: HTMLElement,
    private onFrame: (tracks: Track[]) => void,
    private onModeChange: (live: boolean, label: string) => void,
  ) {
    this.el = this.build();
    container.appendChild(this.el);
  }

  get isLive() { return this.live; }
  toggle() { this.open = !this.open; this.el.classList.toggle('open', this.open); }

  /** Kalles fra main på hver live tracks-oppdatering. Tar kun opp når LIVE. */
  record(tracks: Track[], ts: number) {
    if (!this.live) return;
    this.frames.push({ ts, tracks: tracks.slice() });
    if (this.frames.length > this.cap) this.frames.shift();
    if (this.open) this.syncSlider();
  }

  private build(): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'timemachine';

    this.liveBtn = btn('● LIVE', () => this.goLive());
    this.liveBtn.classList.add('tm-live', 'active');
    this.playBtn = btn('⏵', () => this.togglePlay());
    this.speedBtn = btn('4×', () => this.cycleSpeed());

    this.slider = document.createElement('input');
    this.slider.type = 'range'; this.slider.min = '0'; this.slider.max = '0'; this.slider.value = '0';
    this.slider.className = 'tm-slider';
    this.slider.oninput = () => { this.enterScrub(); this.idx = Number(this.slider.value); this.renderIdx(); };

    this.clockEl = document.createElement('span');
    this.clockEl.className = 'tm-clock';
    this.clockEl.textContent = 'LIVE';

    bar.append(this.liveBtn, this.playBtn, this.speedBtn, this.slider, this.clockEl);
    return bar;
  }

  private syncSlider() {
    const n = this.frames.length;
    this.slider.max = String(Math.max(0, n - 1));
    if (this.live) this.slider.value = this.slider.max;
  }

  private goLive() {
    this.stopPlay();
    this.live = true;
    this.idx = -1;
    this.liveBtn.classList.add('active');
    this.clockEl.textContent = 'LIVE';
    this.syncSlider();
    this.onModeChange(true, '');
  }

  private enterScrub() {
    if (this.live) {
      this.live = false;
      this.liveBtn.classList.remove('active');
    }
  }

  private renderIdx() {
    const f = this.frames[this.idx];
    if (!f) return;
    this.onFrame(f.tracks);
    const t = new Date(f.ts).toISOString().slice(11, 19);
    this.clockEl.textContent = `⏱ ${t}Z`;
    this.onModeChange(false, `AVSPILLING ${t}Z — ikke live`);
  }

  private togglePlay() {
    if (this.playing) { this.stopPlay(); return; }
    if (!this.frames.length) return;
    this.enterScrub();
    if (this.idx < 0 || this.idx >= this.frames.length - 1) this.idx = 0;
    this.playing = true;
    this.playBtn.textContent = '⏸';
    // Avspiller frames med valgt hastighet (frames er ~ett per live-tick).
    this.timer = setInterval(() => {
      if (this.idx >= this.frames.length - 1) { this.goLive(); return; }
      this.idx += 1;
      this.slider.value = String(this.idx);
      this.renderIdx();
    }, Math.max(80, 1000 / SPEEDS[this.speedIdx]));
  }
  private stopPlay() {
    this.playing = false;
    this.playBtn.textContent = '⏵';
    if (this.timer) { clearInterval(this.timer); this.timer = undefined; }
  }
  private cycleSpeed() {
    this.speedIdx = (this.speedIdx + 1) % SPEEDS.length;
    this.speedBtn.textContent = `${SPEEDS[this.speedIdx]}×`;
    if (this.playing) { this.stopPlay(); this.togglePlay(); } // restart med ny hastighet
  }
}

function btn(text: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button'; b.className = 'tm-btn'; b.textContent = text; b.onclick = onClick;
  return b;
}
